import { ChatError, ErrorCode } from 'modules/chatbot/types/chatbot.types';

// Error message mappings for user-friendly display
export const ERROR_MESSAGES: Record<
  ErrorCode,
  {
    userMessage: string;
    technicalMessage: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
  }
> = {
  NETWORK_ERROR: {
    userMessage:
      'Unable to connect to the chat service. Please check your internet connection and try again.',
    technicalMessage: 'Network connection failed',
    severity: 'high',
  },
  TIMEOUT_ERROR: {
    userMessage:
      'The request is taking longer than expected. Please try again.',
    technicalMessage: 'Request timeout exceeded',
    severity: 'medium',
  },
  VALIDATION_ERROR: {
    userMessage: 'Please check your message and try again.',
    technicalMessage: 'Input validation failed',
    severity: 'low',
  },
  SERVER_ERROR: {
    userMessage:
      'Our chat service is temporarily unavailable. Please try again in a few moments.',
    technicalMessage: 'Server returned an error response',
    severity: 'high',
  },
  RATE_LIMIT_ERROR: {
    userMessage:
      "You're sending messages too quickly. Please wait a moment before trying again.",
    technicalMessage: 'Rate limit exceeded',
    severity: 'medium',
  },
  AUTHENTICATION_ERROR: {
    userMessage:
      'Your session has expired. Please refresh the page and try again.',
    technicalMessage: 'Authentication failed',
    severity: 'high',
  },
  CHUNK_DECODING_ERROR: {
    userMessage:
      'We couldn’t decode the full response. Please try again or refresh.',
    technicalMessage: 'Message stream chunk decoding error',
    severity: 'medium',
  },
  SSE_CONNECTION_ERROR: {
    userMessage:
      'Unable to establish a connection to the chat service. Please try again.',
    technicalMessage: 'SSE connection failed to establish',
    severity: 'high',
  },
  SSE_TIMEOUT_ERROR: {
    userMessage:
      'The chat service stopped responding. Please try sending your message again.',
    technicalMessage:
      'SSE stream timeout - no chunks received within expected timeframe',
    severity: 'medium',
  },
  SSE_MALFORMED_MESSAGE: {
    userMessage:
      'Received an unexpected response from the chat service. Please try again.',
    technicalMessage: 'Malformed SSE message received',
    severity: 'medium',
  },
  SSE_STREAM_INTERRUPTED: {
    userMessage:
      'The connection was interrupted. Please try sending your message again.',
    technicalMessage: 'SSE stream was interrupted unexpectedly',
    severity: 'medium',
  },
  SSE_INCOMPLETE_RESPONSE: {
    userMessage: 'The response was incomplete. Please try again.',
    technicalMessage: 'SSE stream ended without COMPLETE event',
    severity: 'medium',
  },
  CONNECTION_LOST: {
    userMessage: 'Connection to the chat service was lost. Please try again.',
    technicalMessage: 'Connection lost during communication',
    severity: 'high',
  },
  RETRY_EXHAUSTED: {
    userMessage:
      'Unable to complete your request after multiple attempts. Please try again later.',
    technicalMessage: 'Maximum retry attempts exceeded',
    severity: 'high',
  },
  UNKNOWN_ERROR: {
    userMessage: 'An unexpected error occurred. Please try again.',
    technicalMessage: 'Unknown error',
    severity: 'medium',
  },
};

/**
 * Creates a standardized ChatError object
 * @param {ErrorCode} code - The error code identifying the type of error
 * @param {string} technicalMessage - Optional technical error message for debugging
 * @param {Record<string, any>} context - Optional context information about the error
 * @return {ChatError} A standardized ChatError object with user-friendly messaging
 */
export function createChatError(
  code: ErrorCode,
  technicalMessage?: string,
  context?: Record<string, any>
): ChatError {
  const errorInfo = ERROR_MESSAGES[code];

  return {
    code,
    message: technicalMessage || errorInfo.technicalMessage,
    userMessage: errorInfo.userMessage,
    technicalDetails: technicalMessage,
    severity: errorInfo.severity,
    timestamp: Date.now(),
    context,
  };
}

/**
 * Determines error code from HTTP status
 * @param {number} status - The HTTP status code
 * @return {ErrorCode} The corresponding error code
 */
export function getErrorCodeFromStatus(status: number): ErrorCode {
  if (status >= 500) return 'SERVER_ERROR';
  if (status === 429) return 'RATE_LIMIT_ERROR';
  if (status === 401 || status === 403) return 'AUTHENTICATION_ERROR';
  if (status >= 400) return 'VALIDATION_ERROR';
  return 'NETWORK_ERROR';
}

/**
 * Determines error code from fetch error
 * @param {Error} error - The fetch error object
 * @return {ErrorCode} The corresponding error code
 */
export function getErrorCodeFromFetchError(error: Error): ErrorCode {
  if (error.name === 'AbortError') return 'SSE_CONNECTION_ERROR';
  if (error.name === 'TypeError' && error.message.includes('fetch'))
    return 'NETWORK_ERROR';
  return 'UNKNOWN_ERROR';
}

/**
 * Creates timeout promise for race conditions
 * @param {number} ms - Timeout duration in milliseconds
 * @param {ErrorCode} errorCode - Error code to use when timeout occurs
 * @return {Promise<T>} Promise that rejects after the specified timeout
 */
export function createTimeoutPromise<T>(
  ms: number,
  errorCode: ErrorCode = 'TIMEOUT_ERROR'
): Promise<T> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(createChatError(errorCode, `Operation timed out after ${ms}ms`));
    }, ms);
  });
}

/**
 * Wraps a promise with timeout
 * @param {Promise<T>} promise - The promise to wrap with timeout
 * @param {number} timeoutMs - Timeout duration in milliseconds
 * @param {ErrorCode} errorCode - Error code to use when timeout occurs
 * @return {Promise<T>} Promise that resolves/rejects with timeout protection
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorCode: ErrorCode = 'TIMEOUT_ERROR'
): Promise<T> {
  return Promise.race([promise, createTimeoutPromise<T>(timeoutMs, errorCode)]);
}

/**
 * Logs error with appropriate level based on severity
 * @param {ChatError} error - The error to log
 * @param {Record<string, any>} context - Optional additional context for logging
 * @return {void}
 */
export function logError(
  error: ChatError,
  context?: Record<string, any>
): void {
  const logData = {
    code: error.code,
    message: error.message,
    userMessage: error.userMessage,
    severity: error.severity,
    timestamp: new Date(error.timestamp).toISOString(),
    context: { ...error.context, ...context },
  };

  switch (error.severity) {
    case 'critical':
      console.error('CRITICAL CHAT ERROR:', logData);
      break;
    case 'high':
      console.error('CHAT ERROR:', logData);
      break;
    case 'medium':
      console.warn('CHAT WARNING:', logData);
      break;
    case 'low':
      console.info('CHAT INFO:', logData);
      break;
    default:
      console.log('CHAT LOG:', logData);
  }
}
